import HTTPTask from "./HTTPTask";
import JSONJQTransformTask from "./JSONJQTransformTask";
import INLINETask from "./INLINETask";
import KAFKATask from "./KAFKATask";
import EventTask from "./EventTask";
import ForkJoinDynamicTask from "./ForkJoinDynamicTask";
import CardIcon from "./CardIcon";
import CardLabel from "./CardLabel";
import CardStatusBadge from "./CardStatusBadge";
import CardAttemptsBadge from "./CardAttemptsBadge";
import { SimpleTask } from "./SimpleTask";
import DeleteButton from "./DeleteButton";

import { TaskType, TaskStatus } from "../../../../../types";

import theme from "../../../theme";
// import AddPathButton from "./AddPathButton";
import { useContext } from "react";
import { colors } from "../../../../../theme/tokens/variables";
import StartWorkflowTask from "./StartWorkflowTask";
import HTTPPollTask from "./HTTPPollTask";
import { JDBCTask } from "./JDBCTask";
import { DynamicTask } from "./DynamicTask";

export const getCardVariant = (
  type: TaskType,
  status: TaskStatus,
  selected?: boolean
) => {
  type TaskStatusKeys = keyof typeof theme.taskStatusOutline;
  const outlineColor = selected
    ? theme.taskCard.selected.outlineColor
    : theme.taskStatusOutline[status as TaskStatusKeys];

  const isOperator = [
    TaskType.FORK_JOIN_DYNAMIC,
    TaskType.JOIN,
    TaskType.FORK_JOIN,
    TaskType.FORK_JOIN_DYNAMIC,
    TaskType.TERMINATE,
    TaskType.SUB_WORKFLOW,
    TaskType.DYNAMIC,
    TaskType.SET_VARIABLE,
    TaskType.WAIT,
    TaskType.START_WORKFLOW,
  ].includes(type);

  let cardStyles = {};

  const operatorStyles = {
    backgroundColor: theme.taskCard.operators.background,
    border: outlineColor
      ? `3px solid ${outlineColor}`
      : `3px solid transparent`,
    color: theme.taskCard.operators.text,
    borderRadius: "10px",
  };

  const tasksStyles = {
    backgroundColor: theme.taskCard.systemTasks.background,
    color: theme.taskCard.systemTasks.text,
    border: outlineColor
      ? `3px solid ${outlineColor}`
      : `3px solid transparent`,
    borderRadius: "10px",
  };

  cardStyles = isOperator ? operatorStyles : tasksStyles;

  const errorStripesColor = () => {
    if (isOperator) {
      if (status === TaskStatus.CANCELED) {
        return "rgba(251, 164, 4, .25)";
      }
      return "rgba(90, 0, 0, .25)";
    } else {
      if (status === TaskStatus.CANCELED) {
        return "rgba(251, 164, 4, .15)";
      }
      return "rgba(220, 110, 110, .15)";
    }
  };

  switch (status) {
    case TaskStatus.FAILED:
    case TaskStatus.SKIPPED:
    case TaskStatus.CANCELED:
    case TaskStatus.TIMED_OUT:
      cardStyles = {
        ...cardStyles,
        backgroundImage: `linear-gradient( 135deg, rgba(0,0,0,0) 25%, ${errorStripesColor()} 25%, ${errorStripesColor()} 50%, rgba(0,0,0,0) 50%, rgba(0,0,0,0) 75%, ${errorStripesColor()} 75%, ${errorStripesColor()} 100% )`,
        // These are not magic numbers!, see: https://css-tricks.com/no-jank-css-stripes/
        backgroundSize: "56.57px 56.57px",
      };
      break;

    default:
      break;
  }

  const boxShadows = [TaskType.SWITCH, TaskType.DECISION].includes(type)
    ? []
    : ["0 2px 20px rgba(0,0,0,.4)"];

  cardStyles = {
    ...cardStyles,
    boxShadow: boxShadows.join(", "),
  };

  return cardStyles;
};

const getTaskCardContent = (type: TaskType, nodeData: any) => {
  switch (type) {
    case TaskType.HTTP:
      return <HTTPTask nodeData={nodeData} />;
    case TaskType.HTTP_POLL:
      return <HTTPPollTask nodeData={nodeData} />;
    case TaskType.JSON_JQ_TRANSFORM:
      return <JSONJQTransformTask nodeData={nodeData} />;
    case TaskType.INLINE:
      return <INLINETask nodeData={nodeData} />;
    case TaskType.KAFKA_PUBLISH:
      return <KAFKATask nodeData={nodeData} />;
    case TaskType.FORK_JOIN_DYNAMIC:
      return <ForkJoinDynamicTask nodeData={nodeData} />;
    case TaskType.EVENT:
      return <EventTask nodeData={nodeData} />;
    case TaskType.SIMPLE:
      return <SimpleTask nodeData={nodeData} />;
    case TaskType.JDBC:
      return <JDBCTask nodeData={nodeData} />;
    case TaskType.START_WORKFLOW:
      return <StartWorkflowTask nodeData={nodeData} />;
    case TaskType.DYNAMIC:
      return <DynamicTask nodeData={nodeData} />;
    default:
      return null;
  }
};

const TaskCard = ({
  nodeData,
  onClick = () => null,
  isInconsistent,
}: {
  nodeData: any;
  onClick: () => void;
  isInconsistent: boolean;
}) => {
  const mode = "light";
  const darkMode = false;

  const { task, status } = nodeData;
  const { name, type, taskReferenceName } = task;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "10px",
        cursor: isInconsistent ? "not-allowed" : "pointer",
        transition: "box-shadow 250ms",
        transitionDelay: "40ms",
        ...getCardVariant(type, status, nodeData.selected),
      }}
      // onClick={onClick}
    >
      <div
        style={{
          position: "relative",
          padding: "20px",
          boxSizing: "border-box",
          width: "100%",
          height: "100%",
          borderRadius: "10px",
          boxShadow: darkMode ? `0 0 10px gray` : undefined,
          color: darkMode ? colors.gray14 : undefined,
          background: darkMode ? colors.gray04 : undefined,
        }}
      >
        {/* Execution */}
        <CardStatusBadge status={status} />
        {nodeData?.attempts > 1 ? (
          <CardAttemptsBadge attempts={nodeData.attempts} />
        ) : null}

        {/* Definition */}
        {/* <DeleteButton maybeHideData={nodeData} /> */}

        <div style={{ display: "flex", width: "100%" }}>
          <CardIcon type={type} />

          <div
            style={{
              flexGrow: 1,
              overflow: "hidden",
              textAlign: "left",
            }}
          >
            <div
              style={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </div>
            <div
              style={{
                color: "#AAAAAA",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {taskReferenceName}
            </div>
            {/* <div
              style={{
                position: "absolute",
                right: 0,
                left: 0,
                marginLeft: "auto",
                marginRight: "auto",
                textAlign: "center",
                marginTop: "3px",
              }}
            > */}
            {/* {!status && type === TaskType.FORK_JOIN ? (
                <AddPathButton nodeData={nodeData}>Add fork</AddPathButton>
              ) : null} */}
            {/* </div> */}
          </div>

          <CardLabel type={type} />
        </div>
        <div>{getTaskCardContent(type, nodeData)}</div>
      </div>
    </div>
  );
};

export default TaskCard;
